import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import classNames from 'classnames';

import { ContentType, LessonMode } from '../../../Constants';

import flaggedSvg from '../../../images/summaryIcons/flagged.svg';

import htmlService from '../../services/HtmlService';
import lessonService from '../../services/LessonService';
import navMenuService from '../../services/NavMenuService';
import responseService from '../../services/ResponseService';
import UtilsService from '../../services/UtilsService';

import useAccessibilityClick from '../../../hooks/useAccessibilityClick';

import HtmlComponent from '../HtmlComponent';
import ResourceIcon from '../ResourceIcon';

import { register } from '../../../i18n';

const t = register('NavigationBar');

/**
 * A single row in the vertical "GO TO" table-of-contents menu.
 * Mirrors GotoCard navigation/selection behavior but renders as a list row.
 */
const VerticalGotoItem = observer(({ lessonElementId, itemNumber, isLocked, childState }) => {
  const {
    lessonManager,
    responseManager,
    navMenuManager
  } = useContext(MobXProviderContext);

  const rowRef = useRef();
  const textRef = useRef();
  const titleTextRef = useRef();
  const subtitleTextRef = useRef();
  const [truncationState, setTruncationState] = useState({ title: false, subtitle: false });
  const model = lessonManager.getSlideModel(lessonElementId);
  const lessonElement = lessonManager.getLessonElement(lessonElementId);
  const lessonElementState = responseManager.getLessonElementState(lessonElementId);

  const isSelected = lessonManager.isSelected(lessonElementId);

  const showNumber = lessonManager.lessonOptions?.verticalGotoQuestionNumber !== false;
  const showText = lessonManager.lessonOptions?.verticalGotoText !== false;

  const clickHandler = async (event) => {
    htmlService.closeMathTypeModal();

    if (isLocked) {
      if (childState && childState.isDependencyLocked) {
        await responseService.alertDependencyLock(childState);
      } else if (lessonElementState && lessonElementState.isDependencyLocked) {
        await responseService.alertDependencyLock(lessonElementState);
      }
      return;
    }

    // Keep the vertical GO TO panel open; it closes only when the user toggles the tab.
    await navMenuService.navToSlideClickHandler(lessonElementId, {
      fromCardHandler: true,
      fromGotoMenuCard: true,
    });
  };

  useAccessibilityClick(rowRef, clickHandler);

  useEffect(() => {
    if (isSelected && rowRef.current) {
      rowRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isSelected]);

  let text = '';
  let subtitleText = '';
  if (model) {
    if (model.isTitlePage) {
      const lessonTitle = lessonManager.lessonTitle;
      text = (!UtilsService.isNullOrEmpty(lessonTitle))
        ? lessonTitle
        : ContentType.TITLE_PAGE.label;
      if (lessonManager.lessonOptions?.showSubtitleGoto &&
        !UtilsService.isNullOrEmpty(lessonManager.lessonOptions?.subtitle)) {
        subtitleText = lessonManager.lessonOptions.subtitle;
      }
    } else if (model.isSummaryPage) {
      text = t('summaryPageName');
    } else {
      const slideSummary = lessonService.getSlideSummary(lessonElement, model);
      text = ContentType.resolveDisplayLabel(model.type, slideSummary);
    }
  }

  const showItemText = model && (showText || model.isTitlePage || model.isSummaryPage);
  const showGotoSubtitle = model?.isTitlePage && !UtilsService.isNullOrEmpty(subtitleText);
  const isGotoMenuOpen = navMenuManager.gotoMenuOpen;

  useLayoutEffect(() => {
    const getTextElementFromRef = (ref) => (
      ref.current?.querySelector('.vertical-goto-item-text') || ref.current
    );

    const isElementTruncated = (el) => (
      !!el && el.clientWidth > 0 && el.scrollWidth > el.clientWidth + 1
    );

    const checkTruncation = () => {
      if (!isGotoMenuOpen || !showItemText) {
        setTruncationState({ title: false, subtitle: false });
        return;
      }

      if (showGotoSubtitle) {
        setTruncationState({
          title: isElementTruncated(getTextElementFromRef(titleTextRef)),
          subtitle: isElementTruncated(getTextElementFromRef(subtitleTextRef)),
        });
        return;
      }

      setTruncationState({
        title: isElementTruncated(getTextElementFromRef(textRef)),
        subtitle: false,
      });
    };

    let transitionTimerId;
    const scheduleChecks = () => {
      checkTruncation();
      requestAnimationFrame(checkTruncation);
      transitionTimerId = window.setTimeout(checkTruncation, 200);
    };

    scheduleChecks();

    const observedElements = [];
    if (showGotoSubtitle) {
      const titleEl = getTextElementFromRef(titleTextRef);
      const subtitleEl = getTextElementFromRef(subtitleTextRef);
      if (titleEl) {
        observedElements.push(titleEl);
      }
      if (subtitleTextRef.current) {
        observedElements.push(subtitleTextRef.current);
      }
      if (subtitleEl) {
        observedElements.push(subtitleEl);
      }
    } else {
      const textEl = getTextElementFromRef(textRef);
      if (textEl) {
        observedElements.push(textEl);
      }
      if (textRef.current) {
        observedElements.push(textRef.current);
      }
    }

    if (observedElements.length === 0) {
      return () => {
        if (transitionTimerId) {
          window.clearTimeout(transitionTimerId);
        }
      };
    }

    const resizeObserver = new ResizeObserver(scheduleChecks);
    observedElements.forEach((el) => resizeObserver.observe(el));
    if (rowRef.current) {
      resizeObserver.observe(rowRef.current);
    }

    const panelEl = (showGotoSubtitle ? titleTextRef : textRef).current?.closest('.vertical-goto-panel');
    if (panelEl) {
      resizeObserver.observe(panelEl);
    }

    return () => {
      resizeObserver.disconnect();
      if (transitionTimerId) {
        window.clearTimeout(transitionTimerId);
      }
    };
  }, [showItemText, showGotoSubtitle, text, subtitleText, isGotoMenuOpen]);

  if (!model) {
    return (<></>);
  }

  const isActivityWithNoQuestions = model.isActivity && lessonElement?.lessonElementIds?.length === 0;
  const isActivityNoItemNumbers = model.isActivity &&
    lessonElement?.lessonElementIds?.length > 0 &&
    UtilsService.isNullOrEmpty(model.questionRange);
  const showContentTypeIcon = !showText && (
    (!model.isTestItem &&
      (!model.isActivity || isActivityNoItemNumbers || isActivityWithNoQuestions) &&
      !model.isTitlePage &&
      !model.isSummaryPage) ||
    model.type === ContentType.LIKERT.type
  );

  const strippedTitle = (!UtilsService.isNullOrEmpty(text))
    ? UtilsService.stripHtmlTagsAdvanced(text)
    : undefined;
  const strippedSubtitle = (showGotoSubtitle && !UtilsService.isNullOrEmpty(subtitleText))
    ? UtilsService.stripHtmlTagsAdvanced(subtitleText)
    : undefined;
  const strippedRollover = (strippedSubtitle)
    ? `${strippedTitle}: ${strippedSubtitle}`
    : strippedTitle;

  // Title / summary pages are not numbered.
  const renderNumber = showNumber && !model.isTitlePage && !model.isSummaryPage &&
    (itemNumber !== null && itemNumber !== undefined);
  const numberLabel = showText ? `${itemNumber}: ` : `${itemNumber}`;

  const isTitleTruncated = truncationState.title;
  const isSubtitleTruncated = truncationState.subtitle;
  const tooltipText = (!showGotoSubtitle && (!showItemText || isTitleTruncated) && strippedRollover)
    ? strippedRollover
    : undefined;
  const showTeacherFeedbackIcon = (lessonManager.playerMode === LessonMode.REVIEW &&
    lessonElementState && lessonElementState.comment &&
    !UtilsService.isNullOrEmpty(lessonElementState.comment));
  const flagged = responseService.isMarkedForReview(lessonElementId);

  return (
    <li
      ref={rowRef}
      aria-current={isSelected ? 'true' : undefined}
      aria-label={strippedRollover}
      className={classNames('vertical-goto-item', {
        selected: isSelected,
        'is-locked': isLocked,
        'is-title-page': model.isTitlePage,
        'is-summary-page': model.isSummaryPage,
        'has-goto-subtitle': showGotoSubtitle,
      })}
      role='button'
      tabIndex='0'
      title={tooltipText}>
      {renderNumber && (
        <span className='vertical-goto-item-number'>{numberLabel}</span>
      )}
      {showContentTypeIcon && (
        <ResourceIcon altText={ContentType.getLabel(model.type)} className={model.type} />
      )}
      {(showItemText && showGotoSubtitle) && (
        <span className='vertical-goto-item-text-wrapper has-goto-subtitle'>
          <span
            ref={titleTextRef}
            className='vertical-goto-item-title-wrapper'
            title={isTitleTruncated ? strippedTitle : undefined}>
            <HtmlComponent
              className='vertical-goto-item-text'
              ariaHidden={true}
              htmlStr={text}
              useSpan={true}
            />
          </span>
          <span
            ref={subtitleTextRef}
            className='vertical-goto-item-subtitle-wrapper'
            title={isSubtitleTruncated ? strippedSubtitle : undefined}>
            <HtmlComponent
              className='vertical-goto-item-text vertical-goto-item-subtitle'
              ariaHidden={true}
              htmlStr={subtitleText}
              useSpan={true}
            />
          </span>
        </span>
      )}
      {(showItemText && !showGotoSubtitle) && (
        <span
          ref={textRef}
          className='vertical-goto-item-text-wrapper'
          title={isTitleTruncated ? strippedRollover : undefined}>
          <HtmlComponent
            className='vertical-goto-item-text'
            ariaHidden={true}
            htmlStr={text}
            useSpan={true}
          />
        </span>
      )}
      {flagged && (
        <img alt='' aria-hidden='true' className='marked-for-review-icon' src={flaggedSvg} />
      )}
      {showTeacherFeedbackIcon && (
        <ResourceIcon altText='Teacher Feedback' className='TeacherFeedback' maskOff={true} />
      )}
    </li>
  );
});

export default VerticalGotoItem;
